import { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { createMap, fmtDuration } from "./mapUtils";
import { PersonSimpleRun, Bicycle, PersonSimpleWalk, Trash, CaretLeft } from "@phosphor-icons/react";

const sportIcon = { run: PersonSimpleRun, bike: Bicycle, walk: PersonSimpleWalk };
const sportColor = { run: "#00e676", bike: "#0084ff", walk: "#a970ff" };

function TrackDetail({ id, onBack }) {
  const [track, setTrack] = useState(null);
  const mapEl = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    api.get(`/tracks/${id}`).then((r) => setTrack(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!track || !mapEl.current) return;
    mapRef.current = createMap(mapEl.current);
    const latlngs = track.points.map((p) => [p.lat, p.lng]);
    if (latlngs.length) {
      L.polyline(latlngs, { color: sportColor[track.sport] || "#0084ff", weight: 4 }).addTo(mapRef.current);
      mapRef.current.fitBounds(L.latLngBounds(latlngs).pad(0.15));
    }
    return () => mapRef.current?.remove();
  }, [track]);

  if (!track) return <div className="glass-card h-40 animate-pulse" />;
  return (
    <div className="space-y-3" data-testid="track-detail">
      <div className="flex items-center gap-2">
        <button onClick={onBack} data-testid="track-back-btn" className="p-1.5 rounded-md hover:bg-secondary transition-colors"><CaretLeft size={18} /></button>
        <div>
          <div className="font-heading font-bold">{track.sport_ru} · {track.user_name}</div>
          <div className="text-xs text-muted-foreground">{new Date(track.started_at).toLocaleString("ru-RU", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}</div>
        </div>
      </div>
      <div ref={mapEl} className="h-[40vh] sm:h-[50vh] rounded-xl overflow-hidden border border-white/[0.08]" data-testid="track-map" />
      <div className="grid grid-cols-4 gap-2">
        {[["Дистанция", `${(track.distance_m / 1000).toFixed(2)} км`],
          ["Время", fmtDuration(track.duration_s)],
          ["Ср. скорость", `${track.avg_speed_kmh} км/ч`],
          ["Макс.", `${track.max_speed_kmh} км/ч`]].map(([l, v]) => (
          <div key={l} className="glass-card p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
            <div className="font-heading text-lg font-extrabold num">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TracksList({ refreshKey }) {
  const { user } = useAuth();
  const [tracks, setTracks] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => setTracks([]));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const remove = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/tracks/${id}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (selected) return <TrackDetail id={selected} onBack={() => setSelected(null)} />;
  if (!tracks) return <div className="glass-card h-32 animate-pulse" />;
  if (tracks.length === 0) return (
    <p className="text-sm text-muted-foreground" data-testid="tracks-empty">
      Пока нет сохранённых маршрутов. Запишите первую тренировку во вкладке «Тренировка».
    </p>
  );

  return (
    <div className="space-y-2" data-testid="tracks-list">
      {tracks.map((t) => {
        const Icon = sportIcon[t.sport] || PersonSimpleRun;
        return (
          <button key={t.id} onClick={() => setSelected(t.id)} data-testid={`track-${t.id}`}
            className="w-full glass-card p-4 flex items-center gap-3 text-left">
            <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
              style={{ background: `${sportColor[t.sport]}22`, color: sportColor[t.sport] }}>
              <Icon size={18} weight="duotone" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{t.sport_ru} · <span className="num">{(t.distance_m / 1000).toFixed(2)} км</span></div>
              <div className="text-[11px] text-muted-foreground truncate">
                {t.user_name} · {new Date(t.started_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })} · {fmtDuration(t.duration_s)} · ср. {t.avg_speed_kmh} км/ч
              </div>
            </div>
            {t.user_id === user?.id && (
              <span onClick={(e) => remove(e, t.id)} data-testid={`track-delete-${t.id}`}
                className="p-1.5 text-muted-foreground hover:text-red-400 transition-colors"><Trash size={15} /></span>
            )}
          </button>
        );
      })}
    </div>
  );
}
